// Due date editing for reminders. <input type="datetime-local"> works in the device's zone; the
// app always means Ljubljana time, so values are converted explicitly.
import { useEffect, useId, useRef, useState } from 'react';
import { TIME_ZONE } from '../lib/format';
import { Icon } from './Icon';

const pad = (n: number) => String(n).padStart(2, '0');

function zonedParts(d: Date, timeZone: string): { y: number; mo: number; d: number; h: number; mi: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute') };
}

/** ISO instant -> "YYYY-MM-DDTHH:mm" wall-clock time in Ljubljana ('' for null/invalid). */
export function toZonedInputValue(iso: string | null | undefined, timeZone = TIME_ZONE): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, timeZone);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** Offset (ms) of `timeZone` from UTC at instant `utcMs`. */
function zoneOffset(utcMs: number, timeZone: string): number {
  const p = zonedParts(new Date(utcMs), timeZone);
  const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  return asUtc - Math.floor(utcMs / 60_000) * 60_000;
}

/** "YYYY-MM-DDTHH:mm" in Ljubljana -> ISO instant (UTC); null when the value is not valid. */
export function fromZonedInputValue(value: string, timeZone = TIME_ZONE): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  if (Number.isNaN(wall)) return null;
  // Two passes settle the offset across DST transitions.
  let utc = wall - zoneOffset(wall, timeZone);
  utc = wall - zoneOffset(utc, timeZone);
  return new Date(utc).toISOString();
}

interface DueEditorProps {
  dueAt: string | null;
  onSave: (dueAt: string | null) => void;
  onClose: () => void;
}

/** Inline editor shown when the due chip is tapped. */
export function DueEditor({ dueAt, onSave, onClose }: DueEditorProps) {
  const id = useId();
  const [value, setValue] = useState(() => toZonedInputValue(dueAt) || toZonedInputValue(defaultDue()));
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);

  const save = () => {
    const iso = fromZonedInputValue(value);
    if (iso) onSave(iso);
    onClose();
  };

  return (
    <div
      className="due-editor"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="due-editor__quick" role="group" aria-label="Hitra izbira roka">
        {quickChoices(dueAt).map((q) => (
          <button
            key={q.label}
            type="button"
            className="btn btn--secondary btn--small"
            onClick={() => {
              onSave(q.iso);
              onClose();
            }}
          >
            {q.label}
          </button>
        ))}
      </div>
      <label htmlFor={id} className="small">
        Rok (ljubljanski čas)
      </label>
      <input
        ref={inputRef}
        id={id}
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
        }}
      />
      <div className="due-editor__actions">
        <button type="button" className="btn btn--primary btn--small" onClick={save} disabled={!fromZonedInputValue(value)}>
          <Icon name="check" size={18} /> Shrani
        </button>
        {dueAt && (
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => {
              onSave(null);
              onClose();
            }}
          >
            Odstrani rok
          </button>
        )}
        <button type="button" className="btn btn--ghost btn--small" onClick={onClose}>
          Prekliči
        </button>
      </div>
    </div>
  );
}

/** Suggested due time for a reminder without one: tomorrow at 9:00. */
function defaultDue(): string {
  const tomorrow = toZonedInputValue(new Date(Date.now() + 86_400_000).toISOString()).slice(0, 10);
  return fromZonedInputValue(`${tomorrow}T09:00`) ?? new Date().toISOString();
}

/** `days` after `from` (midnight-safe: by the Ljubljana calendar), at `time` ("09:00") or the same time. */
function shiftDays(fromIso: string, days: number, time?: string): string {
  const local = toZonedInputValue(fromIso);
  const [date, clock] = local.split('T') as [string, string];
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1, d + days));
  const day = target.toISOString().slice(0, 10);
  return fromZonedInputValue(`${day}T${time ?? clock}`) ?? fromIso;
}

/** Next Monday 9:00 after `fromIso` (Ljubljana). */
function nextMonday(fromIso: string): string {
  const [date] = toZonedInputValue(fromIso).split('T') as [string];
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  const ahead = ((8 - weekday) % 7) || 7;
  return shiftDays(fromIso, ahead, '09:00');
}

/**
 * One-tap choices: tomorrow 9:00 and next Monday 9:00 from today, and – for a reminder that has a
 * due date – the same time a day or a week later ("preloži").
 */
export function quickChoices(dueAt: string | null, now: Date = new Date()): Array<{ label: string; iso: string }> {
  const today = now.toISOString();
  const choices = [
    { label: 'Jutri 9:00', iso: shiftDays(today, 1, '09:00') },
    { label: 'Naslednji teden', iso: nextMonday(today) },
  ];
  if (dueAt && !Number.isNaN(Date.parse(dueAt))) {
    choices.push({ label: '+1 dan', iso: shiftDays(dueAt, 1) }, { label: '+1 teden', iso: shiftDays(dueAt, 7) });
  }
  return choices;
}
